import { ChevronDown } from "lucide-react";
import { ErrorMessage } from "./error-message";
import { ScrollBarArea } from "./scroll-bar-area";
import { classNames } from "~/utils/formatters";
import { useField } from "remix-validated-form";
import { usePopper } from "react-popper";
import type { Dispatch, SetStateAction } from "react";
import { Fragment, useRef, useState } from "react";
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Portal,
  Transition,
} from "@headlessui/react";

type CommonSelectProps = {
  panelWidth: string;
  label: string;
  name: string;
  showLabel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
};

type ControlledSelect = {
  defaultValue?: never;
  state: string;
  onChange: (e: string) => void | Dispatch<SetStateAction<string>>;
} & CommonSelectProps;

type UncontrolledSelect = {
  defaultValue?: string;
  state?: never;
  onChange?: never;
} & CommonSelectProps;

const SelectValidated = ({
  panelWidth,
  label,
  name,
  placeholder,
  required,
  showLabel = false,
  options = [],
  disabled = false,
  defaultValue = "",
  state,
  onChange,
}: ControlledSelect | UncontrolledSelect) => {
  const { error, setTouched, validate } = useField(name, {
    validationBehavior: {
      initial: "onBlur",
      whenSubmitted: "onChange",
      whenTouched: "onChange",
    },
  });

  const [internalSelected, setInternalSelected] = useState(defaultValue);

  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);

  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
  });

  return (
    <Fragment>
      <input
        aria-hidden
        tabIndex={-1}
        className="sr-only inset-x-0 bottom-0"
        style={{
          margin: "0 auto",
        }}
        ref={hiddenInputRef}
        name={name}
        value={state ?? internalSelected}
        onChange={() => null}
        required={required}
        disabled={disabled}
      />
      <Listbox
        as={Fragment}
        value={state ?? internalSelected}
        disabled={disabled}
        onChange={(e) => {
          validate();
          onChange ? onChange(e) : setInternalSelected(e);
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            "value",
          )?.set;
          nativeInputValueSetter?.call(hiddenInputRef.current, e);
          const inputEvent = new Event("change", { bubbles: true });
          hiddenInputRef.current?.dispatchEvent(inputEvent);
        }}
      >
        <Label
          className={
            showLabel ? "mb-2 inline-block text-lg font-medium" : "sr-only"
          }
        >
          {label}
        </Label>
        <div className="relative">
          <ListboxButton
            onBlur={() => {
              setTouched(true);
              validate();
            }}
            onClick={(e) => e.stopPropagation()}
            ref={setReferenceElement}
            style={{
              backgroundImage:
                "radial-gradient(circle at 50% 50%, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.3))",
            }}
            className={classNames(
              "block w-full rounded-3xl border border-opacity-30 bg-transparent bg-clip-padding px-3 py-3 transition focus:ring-1",
              "relative block w-full rounded-3xl border border-opacity-30 bg-transparent bg-clip-padding px-3 py-3 transition focus:ring-1",
              error
                ? "border-red-400 hover:border-red-400 focus:border-red-400 focus:ring-red-400"
                : "border-white hover:border-sky-400 focus:border-sky-400 focus:outline-none  focus:ring-sky-400",
            )}
          >
            {({ value, open }) => (
              <Fragment>
                <span
                  className={classNames(
                    value === "" && "text-opacity-60",
                    "block truncate text-start text-base text-white",
                  )}
                >
                  {value
                    ? options.find((option) =>
                        state
                          ? option.value === state
                          : option.value === internalSelected,
                      )?.label
                    : placeholder ?? "Select an option"}
                </span>
                <span
                  aria-hidden
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5"
                >
                  <ChevronDown
                    className={classNames(
                      open && "rotate-180",
                      value === "" && "text-opacity-60",
                      "h-5 w-5 text-white transition-transform duration-500",
                    )}
                  />
                </span>
              </Fragment>
            )}
          </ListboxButton>
          <Portal>
            <Transition
              ref={setPopperElement}
              as="div"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              className={classNames(
                "z-40 overflow-hidden rounded-3xl border border-white border-opacity-30 bg-clip-padding before:absolute before:inset-0 before:-z-10 before:bg-black before:bg-opacity-50 before:backdrop-blur-lg",
                panelWidth,
              )}
              style={styles.popper}
              {...attributes.popper}
            >
              <ScrollBarArea
                style={{
                  backgroundImage:
                    "linear-gradient(122deg, rgba(17, 24, 39, 0.7) 6%, rgba(255, 255, 255, 0.15) 70%, rgba(255, 255, 255, 0.12) 90%, rgba(17, 24, 39, 0.1) 99%)",
                }}
              >
                <ListboxOptions
                  onClick={(e) => e.stopPropagation()}
                  className="max-h-44 text-sm text-white focus:outline-none"
                >
                  {options.length === 0 ? (
                    <div className="px-6 py-4">Nothing found.</div>
                  ) : (
                    options.map((option) => (
                      <ListboxOption
                        key={option.value}
                        className={({ active }) =>
                          classNames(
                            active && "bg-sky-400",
                            "px-6 py-4 transition-colors",
                          )
                        }
                        value={option.value}
                      >
                        {option.label}
                      </ListboxOption>
                    ))
                  )}
                </ListboxOptions>
              </ScrollBarArea>
            </Transition>
          </Portal>
          {error ? <ErrorMessage error={error} /> : null}
        </div>
      </Listbox>
    </Fragment>
  );
};

if (process.env.NODE_ENV === "development") {
  SelectValidated.displayName = "Input";
}

export { SelectValidated };
